/**
 * @file Redux mappers for compose header container
 */

import { MapDispatchToProps, MapStateToProps } from 'react-redux';

import AppState from '../../../models/state';

import { clearDraftRequest } from '../../../store/actions/draftsMain';
import { getIsDefaultDraftChanged } from '../../compose/helpers';

import { DispatchProps, OwnProps, StateProps } from './types';

/**
 * Map Redux app state to component props
 *
 * @param appState                   Current app state
 * @param appState.debug             Debug information in the state
 * @param appState.drafts            Drafts in the state
 * @param appState.draftsAttachments Attachments drafts in the state
 * @param ownProps                   Directly passed props
 * @param ownProps.nodeType          The type of the node (MESSAGE or TEMPLATE)
 * @returns                          Mapped state
 */
export const mapStateToProps: MapStateToProps<
  StateProps,
  OwnProps,
  AppState
> = ({ debug, drafts, draftsAttachments }, { nodeType }) => {
  const draft = drafts.get(nodeType);
  const attachmentDraft = draftsAttachments.get(nodeType);

  if (draft === undefined || attachmentDraft === undefined) {
    return {
      draft: null,
      isDefaultDraftChanged: false,
      networkTopicBasic: debug.networkTopicBasic,
      topicId: null,
    };
  }

  return {
    draft,
    isDefaultDraftChanged: getIsDefaultDraftChanged(draft, attachmentDraft),
    networkTopicBasic: debug.networkTopicBasic,
    topicId: draft.templateTopicSaved,
  };
};

/**
 * Map Redux dispatch to compose props
 *
 * @param dispatch          Redux dispatch
 * @param ownProps          Directly passed props
 * @param ownProps.nodeType The type of the node (MESSAGE or TEMPLATE)
 * @returns                 Props to add to compose
 */
export const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (
  dispatch,
  { nodeType },
) => ({
  /**
   * Dispatches clearing draft request
   *
   * @returns void
   */
  requestDraftClear: () => dispatch(clearDraftRequest(nodeType)),
});
